import React, { useCallback } from "react";

import { defineMessages, useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import CustomFieldValue from "@mapmycustomers/shared/types/customField/CustomFieldValue";
import { EntityTypesSupportingBulkEdit } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { OptionalFields } from "@mapmycustomers/shared/util/ts";

import Checkbox from "@app/component/input/Checkbox";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import { PersonFieldName } from "@app/util/fieldModel/PersonFieldModel";
import { TripFieldName } from "@app/util/fieldModel/tripFieldModel";

import CustomFieldsEditor from "./CustomFieldsEditor";
import styles from "./FieldEditor.module.scss";
import GeneralFieldsEditor from "./GeneralFieldsEditor";

interface Props {
  entityType: EntityTypesSupportingBulkEdit;
  field: IField;
  onChange: (value: unknown) => void;
  onChangePrimaryEntityId: (id: number) => void;
  value: unknown;
}

const messages = defineMessages({
  clearCheckbox: {
    id: "view.selectBar.button.bulkEdit.panel.clearCheckbox",
    defaultMessage: "Clear all values",
    description: "Bulk Edit button on a selected entities bar - panel - clear checkbox",
  },
  clearCheckboxTooltip: {
    id: "view.selectBar.button.bulkEdit.panel.clearCheckbox.tooltip",
    defaultMessage: "Required fields will be skipped.",
    description: "Bulk Edit button on a selected entities bar - panel - clear checkbox - tooltip",
  },
  description: {
    id: "view.selectBar.button.bulkEdit.panel.description",
    defaultMessage:
      "All selected {entityType, select, trips {drives} other {records}} will be updated based on entered value(s)",
    description: "Bulk Edit button on a selected entities bar - panel - description",
  },
});

const FieldEditor: React.FC<Props> = ({
  entityType,
  field,
  onChange,
  onChangePrimaryEntityId,
  value,
}) => {
  const intl = useIntl();

  const handleClearChange = useCallback(
    (checked: boolean) => {
      if (!checked) {
        onChange(undefined);
        return;
      }

      if (isCustomField(field)) {
        const definition = field.customFieldData;
        onChange({
          crmPropertyKey: definition.crmPropertyKey,
          currencyId: definition.currencyId,
          customField: { id: definition.id },
          dataType: definition.dataType,
          esKey: definition.esKey,
          options: definition.options,
          value: [],
        });
      } else {
        if (field.name === PersonFieldName.COMPANIES) {
          onChange([]);
        } else {
          onChange(null);
        }
      }
    },
    [onChange, field]
  );

  // if field can be nulled and if field is not Frequency Interval, because frequency interval
  // editor already has an option to reset the value. Also not a Trip Classification field,
  // because its editor also has all necessary options.
  const resetSupported =
    field.isNullable &&
    !field.hasAnyFeature(FieldFeature.FREQUENCY_INTERVAL) &&
    !(entityType === EntityType.TRIP && field.name === TripFieldName.TYPE);

  const isClearChecked = resetSupported
    ? isCustomField(field)
      ? Array.isArray((value as CustomFieldValue | undefined)?.value)
        ? !((value as CustomFieldValue | undefined)?.value as Array<any>).length
        : false
      : value === null || (Array.isArray(value) && !value.length)
    : false;

  return (
    <div className={styles.container}>
      <div>
        {isCustomField(field) ? (
          <CustomFieldsEditor
            customField={field}
            disabled={isClearChecked}
            onChange={onChange}
            value={value as OptionalFields<CustomFieldValue, "id"> | undefined}
          />
        ) : (
          <GeneralFieldsEditor
            disabled={isClearChecked}
            entityType={entityType}
            field={field}
            onChange={onChange}
            onChangePrimaryEntityId={onChangePrimaryEntityId}
            value={value}
          />
        )}
      </div>

      {resetSupported && (
        <div className={styles.clear}>
          <Checkbox checked={isClearChecked} onChange={handleClearChange}>
            {intl.formatMessage(messages.clearCheckbox)}
          </Checkbox>
          <TextWithInfo info={intl.formatMessage(messages.clearCheckboxTooltip)} />
        </div>
      )}

      <div className={styles.description}>
        {intl.formatMessage(messages.description, { entityType })}
      </div>
    </div>
  );
};

export default FieldEditor;
